<template>
    <div class="editor-cog-wrapper" v-if="editorEnabled">
        <div style="display: flex;" :title="unsavedConfig ? 'Unsaved Changes' : 'Open Settings'" @mouseenter="setHover()"  @mouseleave="unsetHover()">
            <div class="editor-cog-main-button" @click="getEditor({cId: componentId, toggle: true, previewId})">
                <span class="e-icons e-cog edit-settings"></span>
            </div>
            <sam-tooltip ref="tooltip" position="RightBottom" v-if="showTooltip" :teleport-id="tooltipTeleportId" @update:beforeClose="closeTooltip" css-class="editor-cog-popup" opensOn="Click" :id="componentId + '_cog_tooltip'">
                <div class="editor-expand-button" title="More Options" @click="toggleTooltip">
                    <span class="e-icons e-right-chevron"></span>
                </div>
                <teleport v-if="expandedOptions" :to="'#' + tooltipTeleportId" class="editor-cog-other-actions tooltip-content-slot">
                    <div @mouseleave="unsetHover(true)" @mouseenter="setHover()" class="cog-tooltip-inner">
                        <div class="editor-option editor-cog-view-json" @click="getEditor({cId: componentId, toggle: true, previewId, presetView: 'json'})">
                            View JSON
                        </div>
                        <div class="editor-option editor-cog-copy" @click="copyComponent">Copy Component</div>
                        <div class="editor-option editor-cog-import" @click="importComponent([componentId])">Import</div>
                        <div class="editor-option editor-cog-export" @click="exportComponent([componentId])">Export</div>
                        <div v-show="typeof IS_DEVDASH_QA !== 'undefined'" class="editor-option editor-cog-copy" @click="copyTo">{{IS_DEVDASH_QA ? 'Copy to DevDash' : 'Copy to DevDash QA'}}</div>
                        <div class="editor-option editor-cog-delete" @click="deleteComponent">Delete</div>
                    </div>
                </teleport>
            </sam-tooltip>
            <div v-show="unsavedConfig" class="other-info">!</div>
        </div>
    </div>
</template>
<script>
import { mapActions } from "vuex";
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import SamTooltipComponent from "@common/control/samTooltip.vue";
import { defineAsyncComponent } from "vue";

const settingsCogComponent = {
    name: 'settingsCog',
    props: {
        componentId: {
            default: false
        },
        testMode: {
            default: false
        },
        editorEnabled: {
            default: false
        },
        previewId: {
            default: false
        },
        unsavedConfig: {
            default: false
        }
    },
    data() {
        return {
            clicked: false,
            showTooltip: false,
            hoverTimeout: false,
            expandedOptions: false,
            uniqueId: library.getUniqueID(),
            IS_DEVDASH_QA
        };
    },
    computed: {
        tooltipTeleportId() {
            return 'editor_cog_tooltip_' + this.uniqueId;
        },
        tooltipClosed() {
            return !this.expandedOptions;
        }
    },
    methods: {
        ...mapActions({
            getEditor: 'settingsEditorStore/getEditor',
            importComponent: 'componentStore/importComponent',
            exportComponent: 'componentStore/exportComponent',
        }),
        closeTooltip() {
            this.expandedOptions = false;
        },
        toggleTooltip() {
            this.expandedOptions = !this.expandedOptions;
        },
        setHover() {
            if (!this.showTooltip) this.showTooltip = true;
        },
        unsetHover(hideAll) {
            if (hideAll) this.expandedOptions = false;
            if (!this.expandedOptions) this.showTooltip = false;
        },
        async copyComponent() {
            let component = await componentStore.getConfig(this.componentId, false, true);
            let passedParams = { componentToCopy: this.componentId, createFromCopy: true, cType: component.cType };
            await actionStore.createComponent(passedParams, false, { dialogTitle: 'Copy Component', submitButtonLabel: 'Copy Component' });
        },
        async copyTo() {
            let instance = this.IS_DEVDASH_QA ? 'devDash' : 'devDashQA';
            let componentConfig = await componentStore.getConfig(this.componentId);
            let newCompId = this.componentId;
            let createCopy = false;
            let dialogName = "component-copy-to-template_" + library.getUniqueID();
            let secretValue = '';
            let copyToDialogConfig = {
                name: dialogName,
                template: `<div id="componentCopyToDialogConfig">
                                <control name="NEW_COMPONENT_ID" :prop-config="newComponentID" @update:value="updateNewCompId($event)"></control>
                                <control name="CREATE_AS_NEW" :prop-config="updateIfExists" @update:value="updateCreateCopy($event)"></control>
                                <control v-if="showSecret" name="SECRET" :prop-config="secretConfig" :value="secretValue" @update:value="updateSecret($event)"></control>
                            </div>`,
                data() {
                    return {
                        controls: {}, // Holds rendered control configs
                        newComponentID: {
                            name: "NEW_COMPONENT_ID",
                            ref: "NEW_COMPONENT_ID",
                            label: "Component ID",
                            type: "string",
                            value: newCompId
                        },
                        updateIfExists: {
                            name: "CREATE_AS_NEW",
                            ref: "CREATE_AS_NEW",
                            label: "Create as New",
                            inlineLabel: true,
                            message: 'If the component already exists, create a copy.',
                            type: "boolean",
                            value: createCopy
                        },
                        secretValue: secretValue,
                        createCopy: createCopy,
                        secretConfig: {
                            name: 'SECRET',
                            type: 'string',
                            label: 'Please enter the secret key to unlock',
                            helpText: 'Please type UNLOCK in all caps.'
                        }
                    };
                },
                computed: {
                    showSecret() {
                        return componentConfig.lockedConfig && !this.createCopy;
                    }
                },
                methods: {
                    updateNewCompId(event) {
                        newCompId = event;
                    },
                    updateCreateCopy(event) {
                        this.createCopy = event;
                        createCopy = event;
                    },
                    updateSecret(event) {
                        secretValue = event;
                    }
                },
                components: {
                    'control': defineAsyncComponent(() => import('@common/control/control.vue'))
                }
            };
            let funcOnSuccess = async () => {
                if (componentConfig.lockedConfig && !createCopy && secretValue !== 'UNLOCK') {
                    library.showDialog('Please enter the secret key to copy', false, false, { title: 'Warning', showAsAlert: true });
                    return {
                        status: 'failure'
                    };
                }
                if (createCopy && newCompId === this.componentId) {
                    newCompId += '_copy';
                }
                let createOptions = {
                    instance: instance,
                    cId: newCompId,
                    updateIfExists: !createCopy // Note this is reversed here
                };
                let result = await componentStore.createComponent(componentConfig, createOptions);
                return !!(result && typeof result === 'object' && result['status'] === 'success');
            };
            return library.showDialog(copyToDialogConfig, funcOnSuccess, false, { title: 'Copy Component', doNotCloseOnFuncFailure: true });
        },
        async deleteComponent() {
            let iConfig = await componentStore.getConfig(this.componentId);
            if (iConfig.lockedConfig) {
                library.showDialog('You cannot delete this component because it is locked.', false, false, { showAsAlert: true });
                return false;
            }
            library.showDialog("Are you sure you want to delete this component?",
                async () => {
                    const deleted = await componentStore.deleteComponent(this.componentId);
                    if (deleted) {
                        componentStore.getList(true, true);
                    }
                },
                false,
                {
                    title: "Confirm to Continue"
                });
            return true;
        }
    },
    components: {
        'sam-tooltip': SamTooltipComponent
    }
};

export default settingsCogComponent;
</script>

<style lang="css">
.editor-cog-popup.e-tooltip-wrap.e-popup {
    background: #d5d0c9;
    padding: 0;
    border: 0 none;
    margin-top: -19px;
    margin-left: -6px;
    border-radius: 0 10px 10px 10px;
    box-shadow: 2px 2px 4px rgba(0,0,0, 0.1);
}
.editor-cog-popup.e-tooltip-wrap.e-popup .e-arrow-tip {
    display: none;
}
.editor-cog-popup.e-tooltip-wrap.e-popup .e-tip-content {
    margin: 0;
    padding: 0;
    color: black;
}
.cog-tooltip-inner {
    padding: 3px 0;
}
.edit-settings {
    margin-bottom: -3px;
}
.component-settings-editor .e-icons.e-cog:before {
    content: '\e679' !important;
}
.editor-cog-wrapper {
    position: relative;
    top: 0;
    left: 0;
    height: 0;
    width: 0;
    display: flex;
    overflow: visible;
    z-index: 1100;
}
.editor-cog-main-button {
    position: relative;
    z-index: 1;
    border-radius: 50%;
    border: 1px solid #bbb;
    box-shadow: -1px -1px 2px rgb(0 0 0 / 10%);
    height: 22px;
    width: 22px;
    text-align: center;
    line-height: 24px;
    font-size: 14px;
    background-color: #dfd9d0;
    cursor: pointer;
}
.editor-cog-button:hover {
    background-color: #e7e1d8;
}
.editor-cog-button:active {
    background-color: #d8d1c9;
}
.editor-option {
    margin: 0 3px;
    padding: 4px 16px 4px 8px;
    font-size: 15px;
    line-height: 19px;
    border-bottom: 1px solid #c2beba;
    cursor: pointer;
}
.editor-option:last-child {
    border-bottom: 0 none;
    border-radius: 0 0 8px 0;
}
.editor-option:first-child {
    border-radius: 0 8px 0 0;
}
.editor-option:hover, .editor-expand-button:hover {
    background: #efece8;
}
.editor-expand-button {
    /* width: 32px; */
    height: 19px;
    top: 1px;
    position: relative;
    left: -5px;
    border-radius: 0 4px 4px 0;
    padding-left: 4px;
    padding-right: 1px;
    font-size: 13px;
    line-height: 22px;
    font-weight: bold;
    cursor: pointer;
    background: #d5d0c9;
}
.editor-cog-delete {
    margin-top: 5px;
    color: #981212;
}
.other-info {
    width: 26px;
    height: 20px;
    top: 1px;
    position: relative;
    left: -11px;
    padding-left: 15px;
    font-size: 16px;
    line-height: 20px;
    font-weight: bold;
    background-color: #e55871;
    color: white;
    box-shadow: 1px 1px 3px rgba(0,0,0, 0.15) inset;
    border-bottom: 1px solid #f6a1b0;
    border-radius: 0 10px 10px 0;
    transition: width .3s ease-out;
    cursor: pointer;
}
</style>
